import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTalentpoolvoorstel } from '../../api/referentie.ts';
import type { Functietitel, IndelingInvoer, Talentpool } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { FunctietitelsVeld, TalentpoolsVeld } from '../../componenten/keuzevelden.tsx';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  functietitels: Functietitel[];
  talentpools: Talentpool[];
}

interface Props {
  open: boolean;
  onSluit: () => void;
  titel?: string | undefined;
  functietitels: readonly Functietitel[];
  talentpools: readonly Talentpool[];
  opslaan: (invoer: IndelingInvoer) => Promise<unknown>;
}

/**
 * Vervangt de indeling van een kandidaat, cv of vacature in functietitels en talentpools. Bij de gekozen
 * functietitels stelt het systeem talentpools voor; de indeling zelf leg je bewust vast.
 * @param props De huidige indeling en hoe die wordt opgeslagen.
 * @returns De dialoog.
 */
export function IndelingFormulier({ open, onSluit, titel = 'Indeling', functietitels, talentpools, opslaan }: Props) {
  const formulier = useForm<Waarden>({
    defaultValues: { functietitels: [...functietitels], talentpools: [...talentpools] },
  });
  useEffect(() => {
    if (open) {
      formulier.reset({ functietitels: [...functietitels], talentpools: [...talentpools] });
    }
  }, [open, functietitels, talentpools, formulier]);

  const gekozenFunctietitels = formulier.watch('functietitels');
  const gekozenTalentpools = formulier.watch('talentpools');
  const voorstel = useTalentpoolvoorstel(gekozenFunctietitels.map((item) => item.id));
  const ontbrekend = (voorstel.data ?? []).filter(
    (pool) => !gekozenTalentpools.some((gekozen) => gekozen.id === pool.id),
  );

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) =>
      opslaan({
        functietitelIds: waarden.functietitels.map((item) => item.id),
        talentpoolIds: waarden.talentpools.map((item) => item.id),
      }),
    { melding: 'Indeling opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog open={open} titel={titel} onSluit={onSluit} onVerzend={onVerzend} fout={fout} bezig={bezig}>
        <Stack spacing={2}>
          <FunctietitelsVeld naam="functietitels" autoFocus />
          <TalentpoolsVeld naam="talentpools" />
          {ontbrekend.length > 0 && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    formulier.setValue('talentpools', [...gekozenTalentpools, ...ontbrekend], { shouldDirty: true })
                  }
                >
                  Overnemen
                </Button>
              }
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Bij deze functietitels horen standaard:
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {ontbrekend.map((pool) => (
                  <Chip key={pool.id} label={pool.naam} size="small" />
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
